import { createReducer, createSelector } from '@reduxjs/toolkit';
import { all, call, fork, takeEvery } from 'redux-saga/effects';
import {
  FIELDTEMPLATE_INCLUDE,
  FIELDTEMPLATE_TYPE,
  FIELDTYPE_ESDH_TITLE,
} from '../../constants/api';
import {
  createFieldTemplateInitialize,
  createFieldTemplateSuccess,
  deleteFieldTemplateInitialize,
  deleteFieldTemplateSuccess,
  getAllHearingsSuccess,
  getAllHearingTemplatesSuccess,
  getAllHearingTypesSuccess,
  getHearingSuccess,
  getPaginatedHearingsSuccess,
  updateFieldTemplateInitialize,
  updateFieldTemplateSuccess,
} from '../actions/api';
import { FieldTemplateConnector } from '../connectors/fieldTemplateConnector';
import { getAttr, getEntityId, getRelId } from '../helpers/apiHelpers';
import { removeItemFromList, updateDtoList } from '../helpers/reducerHelpers';
import {
  generateCreateApiCall,
  generateUpdateApiCall,
} from '../helpers/sagaHelpers';
import {
  getIncludedData,
  getRequestData,
  selectAllFromApiState,
} from '../helpers/selectorHelpers';
import { allFieldsByHearingTypeIdSelector } from './fieldSlice';
import { allFieldTypesSelector } from './fieldTypeSlice';

// -------- Overview --------

// The following endpoints exist
//  - GET /hearingType/{hearingTypeId}/fieldTemplate
//  - POST /hearingType/{hearingTypeId}/fieldTemplate
//  - PATCH /hearingType/{hearingTypeId}/fieldTemplate/{id}
//  - DELETE /hearingType/{hearingTypeId}/fieldTemplate/{id}
//
// FieldTemplate is included in the following endpoints
//  - GET /hearing
//  - GET /hearing/{hearingId}
//  - GET /hearingType

// -------- Reducer --------
const fieldTemplateReducer = createReducer(
  {
    all: [],
  },
  (builder) => {
    builder
      .addCase(getAllHearingTypesSuccess, (state, action) => {
        const data = getIncludedData(action);
        const fieldTemplates = data.filter(
          (include) => include.type === FIELDTEMPLATE_INCLUDE,
        );
        return updateDtoList(state, 'all', fieldTemplates);
      })
      .addCase(getAllHearingsSuccess, (state, action) => {
        const data = getIncludedData(action);
        const fieldTemplates = data.filter(
          (include) => include.type === FIELDTEMPLATE_INCLUDE,
        );
        return updateDtoList(state, 'all', fieldTemplates);
      })
      .addCase(getHearingSuccess, (state, action) => {
        const data = getIncludedData(action);
        const fieldTemplates = data.filter(
          (include) => include.type === FIELDTEMPLATE_INCLUDE,
        );
        return updateDtoList(state, 'all', fieldTemplates);
      })
      .addCase(getAllHearingTemplatesSuccess, (state, action) => {
        const data = getIncludedData(action);
        const fieldTemplates = data.filter(
          (include) => include.type === FIELDTEMPLATE_TYPE,
        );
        return updateDtoList(state, 'all', fieldTemplates);
      })
      .addCase(createFieldTemplateSuccess, (state, action) => {
        const fieldTemplate = getRequestData(action);
        return updateDtoList(state, 'all', fieldTemplate);
      })
      .addCase(updateFieldTemplateSuccess, (state, action) => {
        const fieldTemplate = getRequestData(action);
        return updateDtoList(state, 'all', fieldTemplate);
      })
      .addCase(deleteFieldTemplateSuccess, (state, action) => {
        const fieldTemplateId = getEntityId(getRequestData(action));
        return removeItemFromList(state, 'all', fieldTemplateId);
      })
      .addCase(getPaginatedHearingsSuccess, (state, action) => {
        const data = getIncludedData(action);
        const fieldTemplates = data.filter(
          (include) => include.type === FIELDTEMPLATE_INCLUDE,
        );
        return updateDtoList(state, 'all', fieldTemplates);
      });
  },
);

// -------- Sagas ----------
export function* fieldTemplateSaga() {
  yield all([
    fork(createFieldTemplateSaga),
    fork(updateFieldTemplateSaga),
    fork(deleteFieldTemplateSaga),
  ]);
}

function* createFieldTemplateSaga() {
  yield takeEvery(
    createFieldTemplateInitialize.type,
    generateCreateApiCall(FieldTemplateConnector, function* (
      connector,
      action,
    ) {
      return yield call(
        [connector, connector.createFieldTemplate],
        action.payload.hearingTypeId,
        action.payload.model,
      );
    }),
  );
}

function* updateFieldTemplateSaga() {
  yield takeEvery(
    updateFieldTemplateInitialize.type,
    generateUpdateApiCall(FieldTemplateConnector, function* (
      connector,
      action,
      requestData,
    ) {
      return yield call(
        [connector, connector.updateFieldTemplate],
        action.payload.hearingTypeId,
        action.payload.fieldTemplateId,
        requestData,
      );
    }),
  );
}

function* deleteFieldTemplateSaga() {
  yield takeEvery(
    deleteFieldTemplateInitialize.type,
    generateUpdateApiCall(FieldTemplateConnector, function* (
      connector,
      action,
    ) {
      return yield call(
        [connector, connector.deleteFieldTemplate],
        action.payload.hearingTypeId,
        action.payload.fieldTemplateId,
      );
    }),
  );
}
// -------- Selectors --------
const selectGetAllFieldTemplates = (state) =>
  selectAllFromApiState(state, 'fieldTemplate');
const allFieldTemplatesSelector = createSelector(
  selectGetAllFieldTemplates,
  (fieldTemplates) => fieldTemplates,
);
const fieldTemplateByHearingTypeIdSelector = (hearingTypeId) =>
  createSelector(allFieldTemplatesSelector, (fieldTemplates) =>
    fieldTemplates.filter(
      (fT) => getRelId(fT, 'hearingType') === hearingTypeId,
    ),
  );
const esdhTemplatesByHearingTypeIdSelector = (hearingTypeId) =>
  createSelector(
    [
      allFieldTemplatesSelector,
      allFieldsByHearingTypeIdSelector(hearingTypeId),
      allFieldTypesSelector,
    ],
    (fieldTemplates, fields, fieldTypes) => {
      const esdhFieldType = fieldTypes.find(
        (f) => getAttr(f, 'fieldType') === FIELDTYPE_ESDH_TITLE,
      );
      const esdhField = fields.find(
        (f) => getRelId(f, 'fieldType') === getEntityId(esdhFieldType),
      );
      const esdhFieldTemplates = fieldTemplates.filter(
        (f) => getRelId(f, 'field') === getEntityId(esdhField),
      );
      return esdhFieldTemplates.filter(
        (f) => getRelId(f, 'hearingType') === hearingTypeId,
      );
    },
  );
const fieldTemplatesByFieldIdAndHearingTypeIdSelector = (
  hearingTypeId,
  fieldId,
) =>
  createSelector([allFieldTemplatesSelector], (fieldTemplates) =>
    fieldTemplates.filter(
      (ft) =>
        getRelId(ft, 'field') === fieldId &&
        getRelId(ft, 'hearingType') === hearingTypeId,
    ),
  );
const fieldTemplateByIdSelector = (id) =>
  createSelector(allFieldTemplatesSelector, (fieldTemplates) =>
    fieldTemplates.find((ft) => getEntityId(ft) === id),
  );
// -------- Exporting --------
export {
  allFieldTemplatesSelector,
  esdhTemplatesByHearingTypeIdSelector,
  fieldTemplateByHearingTypeIdSelector,
  fieldTemplatesByFieldIdAndHearingTypeIdSelector,
  fieldTemplateByIdSelector,
};
export default fieldTemplateReducer;
